import React from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import PublicHeader from './PublicHeader';

const PrivacyPolicyPage = ({ setActiveView, onSupportClick }) => {
  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-6">
      <PublicHeader
        activeViewId="privacy-policy"
        setActiveView={setActiveView}
        onSupportClick={onSupportClick}
      />
      <div className="max-w-4xl mx-auto pt-24 sm:pt-28 pb-10">
        <button
          type="button"
          onClick={() => setActiveView('home')}
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </button>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Privacy Policy</h1>
              <p className="text-sm text-slate-500">FashDeck</p>
            </div>
          </div>

          <p className="text-sm text-slate-600 mb-6">Effective date: March 10, 2026</p>

          <div className="space-y-6 text-slate-700 leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">1. Information We Collect</h2>
              <p>
                We collect information you provide directly, such as account details, uploaded fabric images,
                product information, and content preferences. We may also collect basic technical information like
                device, browser, and usage analytics to improve platform performance.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">2. How We Use Your Information</h2>
              <p>
                FashDeck uses your data to deliver AI-powered fashion generation, save your kits and materials,
                improve feature quality, provide customer support, and maintain account and credit operations.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">3. AI Processing and Uploaded Content</h2>
              <p>
                Images and related inputs may be processed by trusted AI and cloud infrastructure providers to
                generate outputs. We use this processing only to operate requested features and improve reliability.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">4. Sharing and Disclosure</h2>
              <p>
                We do not sell your personal data. We may share limited information with service providers for
                hosting, storage, analytics, and AI generation, or when required by law.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">5. Data Retention</h2>
              <p>
                We retain data for as long as necessary to provide services, comply with legal obligations,
                resolve disputes, and enforce policies. You can request deletion of your account and associated data.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">6. Security</h2>
              <p>
                We use reasonable technical and organizational safeguards to protect your information, but no method
                of transmission or storage is completely secure.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">7. Your Choices</h2>
              <p>
                You can update profile information, manage content you upload, and contact us for privacy-related
                requests. You may also choose not to use optional features.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">8. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Continued use of FashDeck after updates
                constitutes acceptance of the revised policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">9. Contact</h2>
              <p>
                For privacy questions, please contact the FashDeck support team through the channels available in the app.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">10. Request Account Deletion</h2>
              <p className="mb-3">
                To request deletion of your FashDeck account and associated data, use the link below and include the
                email or phone number linked to your account. We will process verified requests according to applicable
                legal and operational requirements.
              </p>
              <a
                href={`mailto:support@instaad.in?subject=${encodeURIComponent('Account Deletion Request')}&body=${encodeURIComponent('Hi FashDeck Support,\n\nI would like to request deletion of my account and associated data.\n\nAccount identifier (email/phone): \n\nAdditional details (optional): \n\nThank you.')}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors font-medium"
              >
                Request account deletion
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
